import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Card } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import { append, head, mergeRight } from "ramda";
import { useSearchQuery } from "../common/use-searchQuery";
import { useSearch } from "../searchServices";
import { notBlank } from "../../util/ext";
import { normalizeFacetResults } from "../../browse-media/redux/browseMediaHelpers";
import { BarChart, Bar, XAxis, ResponsiveContainer, Rectangle } from "recharts";
import { theme } from "theme.js";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { Link, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DateRangeSelect, {
  calculateDate,
  dateRanges,
} from "../../components/dashboard/DateRangeSelect";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    height: 350,
    padding: theme.spacing(4),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      height: "100%",
    },
  },

  h1: {
    fontSize: "3.6rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  graph: {
    marginLeft: -35,
    width: "calc(100% + 75px)",
    height: "100%",
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    color: theme.palette.primary.main,
  },

  newButton: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },

  forwardIcon: {
    color: theme.palette.primary.main,

    "& :first-child": {
      fontSize: "1.2rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
}));

const initialDate = calculateDate(dateRanges.last_30);
const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [
    {
      name: "type",
      queryParam: "_type",
      terms: [
        {
          code: "CONTENT",
        },
      ],
    },
  ],
  orderBy: "name",
  orderDirection: "asc",
  otherParams: [{ aa: `${initialDate}|` }],
  appliedPublicationYearGroups: [],
  limit: 0,
  page: 0,
  noFacets: true,
};

const MediaFilesCard = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [contentTypeFacet, setContentTypeFacet] = useState({});

  //-- Handle Search state
  const { state, handleUpdateOtherParams } = useSearchQuery(initialSearchState);

  const searchResults = useSearch(state);

  const [contentTypes, setContentTypes] = useState([]);
  useEffect(() => {
    if (searchResults.isSuccess && notBlank(searchResults.data.facets)) {
      //-- Get the content type facets
      const facet = searchResults.data.facets.contentType;
      const contentTypesFacet = head(normalizeFacetResults([facet]));
      if (notBlank(contentTypesFacet)) {
        setContentTypeFacet(contentTypesFacet);
        setContentTypes(contentTypesFacet.terms);
      } else {
        setContentTypeFacet(null);
        setContentTypes([]);
      }
    }
  }, [searchResults.isSuccess, searchResults.data, state]);

  const handleContentTypeClick = useCallback(
    data => {
      const facet = {
        name: contentTypeFacet.name,
        queryParam: contentTypeFacet.queryParam,
        terms: [{ code: data.code, label: data.label, count: data.count }],
      };

      const search = mergeRight(state, {
        appliedFacets: append(facet, state.appliedFacets),
      });

      navigate("/creator/content", { state: { search } });
    },
    [state, contentTypeFacet, navigate],
  );

  const handleDateRangeChange = useCallback(
    value => {
      console.log(`Search Date : ${value}`);
      handleUpdateOtherParams([{ aa: `${value}|` }]);
    },
    [handleUpdateOtherParams],
  );

  return (
    <Card classes={{ root: classes.root }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Link to={{ pathname: "/creator/content" }} state={{ search: state }}>
            <Typography variant="h4" component="span">
              Media Files
            </Typography>
          </Link>
        </Grid>
        <Grid item>
          <DateRangeSelect
            initialRange={dateRanges.last_30}
            onDateRangeChange={handleDateRangeChange}
          />
        </Grid>
      </Grid>
      <Box>
        <Typography variant="h1" component="span" classes={{ h1: classes.h1 }}>
          {searchResults.isSuccess
            ? searchResults.data.totalCount.toLocaleString()
            : "Loading..."}
        </Typography>
      </Box>
      {searchResults.isSuccess ? (
        notBlank(contentTypes) ? (
          <Box className={classes.graph}>
            <ResponsiveContainer width="100%" height="70%">
              <BarChart
                data={contentTypes}
                maxBarSize={20}
                margin={{
                  top: theme.spacing(3),
                  right: 0,
                  left: 0,
                  bottom: 0,
                }}
              >
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tickSize={15}
                  tick={{
                    fill: theme.palette.secondary.contrastText,
                    fontSize: 12,
                  }}
                  fontFamily={theme.typography.fontFamily}
                />
                <Bar
                  dataKey="count"
                  fill={theme.palette.primary.main}
                  background={{
                    fill: theme.palette.primary.contrastText,
                    cursor: "pointer",
                  }}
                  shape={<Rectangle radius={20} />}
                  cursor="pointer"
                  label={{
                    position: "top",
                    offset: 10,
                    fill: theme.palette.secondary.contrastText,
                    fontSize: 12,
                  }}
                  onClick={handleContentTypeClick}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            width={325}
            height={45}
            ml={"auto"}
            mr={"auto"}
            mt={12}
            textAlign="center"
          >
            <Typography
              component="div"
              variant="body1"
              classes={{ body1: classes.body1 }}
            >
              No media files have been added in the last 30 days.
            </Typography>
            <Button
              size="small"
              classes={{
                textSizeSmall: classes.newButton,
                iconSizeSmall: classes.forwardIcon,
              }}
              variant="text"
              color="primary"
              endIcon={<ArrowForwardIcon fontSize="small" />}
            >
              Add New Media
            </Button>
          </Box>
        )
      ) : (
        <Box flexGrow={1}>
          <PanelLoadingIndicator />
        </Box>
      )}
    </Card>
  );
};

export default MediaFilesCard;
